import { AFFILIATE_MARKET_TYPES, MARKET_TYPES } from "../../../config/marketType";
import { CurrencyConverter } from "../../../lib/currency-converter";
import { PriceCalculator } from "../../../lib/price-calculator";
import { PriceFormatter } from "../../../utils/priceFormatter";

const MIN_TOTAL_SHIPPING_FEE_THB =
    parseFloat(process.env.REACT_APP_MIN_TOTAL_SHIPPING_FEE_THB) || 1;
const MIN_TOTAL_SHIPPING_FEE_MMK =
    parseFloat(process.env.REACT_APP_MIN_TOTAL_SHIPPING_FEE_MMK) || 500;

const getOrderItems = (
    snapshotItems,
    liveItems,
    snapshotPriceCalculator,
    livePriceCalculator,
    priceFormatter
) => {
    const getAttributePairs = (variant) => {
        return (
            variant?.productAttributeValueDTO?.map(
                (attr) => `${attr.productAttributeName}: ${attr.name}`
            ) || []
        );
    };

    const getSnapshotPrices = (snapshotProduct) => {
        const unitPriceInTHB = snapshotProduct.orderPrice;
        const unitPrice = snapshotPriceCalculator.convertToDualCurrency(unitPriceInTHB);
        const lineTotal = snapshotPriceCalculator.calculateItemTotal(
            unitPriceInTHB,
            snapshotProduct.orderUnit
        );

        const unitOrgPrice = snapshotPriceCalculator.calculateOriginalPrice(
            unitPriceInTHB,
            snapshotProduct.discountPercentage
        );
        const orgPriceTotal = snapshotPriceCalculator.calculateItemTotal(
            unitOrgPrice.THB,
            snapshotProduct.orderUnit
        );

        const unitSellerOrgPriceInTHB = priceFormatter.round(
            Number(snapshotProduct.productVariantDTO?.price) || 0
        );
        const unitSellerOrgPrice =
            snapshotPriceCalculator.convertToDualCurrency(unitSellerOrgPriceInTHB);
        const sellerOrgPriceLineTotal = snapshotPriceCalculator.calculateItemTotal(
            unitSellerOrgPriceInTHB,
            snapshotProduct.orderUnit
        );

        return {
            unitPrice,
            unitOrgPrice,
            lineTotal,
            orgPriceTotal,
            discountPercentage: snapshotProduct.discountPercentage || 0,
            unitSellerOrgPrice,
            sellerOrgPriceLineTotal
        };
    };

    const getLivePrices = (liveProduct) => {
        if (!liveProduct?.productVariantDTO) {
            return null;
        }

        const variant = liveProduct.productVariantDTO;

        const neonDiscountedPrice =
            variant.showNeonDiscountPrice > 0
                ? variant.showNeonDiscountPrice
                : variant.showNeonOrgPrice;

        const unitPriceInTHB = priceFormatter.round(Number(neonDiscountedPrice) || 0);
        const unitPrice = livePriceCalculator.convertToDualCurrency(unitPriceInTHB);
        const lineTotal = livePriceCalculator.calculateItemTotal(
            unitPriceInTHB,
            liveProduct.orderUnit
        );

        const orgPriceInTHB = variant.showNeonOrgPrice;
        const orgPriceTotal = livePriceCalculator.calculateItemTotal(
            orgPriceInTHB,
            liveProduct.orderUnit
        );

        const unitOrgPrice = livePriceCalculator.convertToDualCurrency(orgPriceInTHB);

        const discountPercentage =
            orgPriceInTHB > 0
                ? Math.round(((orgPriceInTHB - unitPriceInTHB) / orgPriceInTHB) * 100)
                : 0;

        const unitSellerPriceInTHB = priceFormatter.round(
            (Number(variant.price) || 0) - (Number(variant.originalDiscountPrice) || 0)
        );
        const unitSellerPrice = livePriceCalculator.convertToDualCurrency(unitSellerPriceInTHB);
        const sellerEarningsLineTotal = livePriceCalculator.calculateItemTotal(
            unitSellerPriceInTHB,
            liveProduct.orderUnit
        );

        const unitSellerOrgPriceInTHB = priceFormatter.round(Number(variant.price) || 0);
        const unitSellerOrgPrice =
            livePriceCalculator.convertToDualCurrency(unitSellerOrgPriceInTHB);
        const sellerOrgPriceLineTotal = livePriceCalculator.calculateItemTotal(
            unitSellerOrgPriceInTHB,
            liveProduct.orderUnit
        );

        return {
            unitPrice,
            unitOrgPrice,
            lineTotal,
            orgPriceTotal,
            discountPercentage,
            unitSellerPrice,
            sellerEarningsLineTotal,
            unitSellerOrgPrice, // ← add
            sellerOrgPriceLineTotal // ← add
        };
    };

    const items = snapshotItems.map((snapshot) => {
        const live = liveItems.find((item) => item.id === snapshot.id);
        return {
            id: snapshot.id,
            name: snapshot.productId?.name,
            nameBurmese: snapshot.productId?.nameBurmese,
            nameThai: snapshot.productId?.nameThai,
            quantity: snapshot.orderUnit,
            image: snapshot.productVariantDTO.imageUrl,
            attributePairs: getAttributePairs(snapshot.productVariantDTO),
            brand: snapshot.productId?.brand || "",
            variant: snapshot.productVariantDTO,
            snapshotPrices: getSnapshotPrices(snapshot),
            livePrices: getLivePrices(live),
            productUrlWebEncoded: snapshot.productId?.productUrlWebEncoded,
            sku: snapshot.productVariantDTO.sku,
            category: {
                id: snapshot.productId?.subSubCategoryId,
                name: snapshot.productId?.categoryName
            }
        };
    });

    return items;
};

const getItemTotals = (items) => {
    return items.reduce(
        (acc, item) => {
            const snapshotDiscountedPriceTotal = item.snapshotPrices.lineTotal;
            const liveDiscountedPriceTotal = item.livePrices?.lineTotal || { THB: 0, MMK: 0 };

            const snapshotOriginalPriceTotal = item.snapshotPrices.orgPriceTotal;
            const liveOriginalPriceTotal = item.livePrices?.orgPriceTotal || { THB: 0, MMK: 0 };

            const snapshotDiscountAmountTotal = {
                THB: snapshotOriginalPriceTotal.THB - snapshotDiscountedPriceTotal.THB,
                MMK: snapshotOriginalPriceTotal.MMK - snapshotDiscountedPriceTotal.MMK
            };
            const liveDiscountAmountTotal = {
                THB: liveOriginalPriceTotal.THB - liveDiscountedPriceTotal.THB,
                MMK: liveOriginalPriceTotal.MMK - liveDiscountedPriceTotal.MMK
            };

            const liveSellerEarningsTotal = item.livePrices?.sellerEarningsLineTotal || {
                THB: 0,
                MMK: 0
            };

            return {
                quantity: acc.quantity + item.quantity,
                live: {
                    orgPrice: {
                        THB: acc.live.orgPrice.THB + liveOriginalPriceTotal.THB,
                        MMK: acc.live.orgPrice.MMK + liveOriginalPriceTotal.MMK
                    },
                    discountedPrice: {
                        THB: acc.live.discountedPrice.THB + liveDiscountedPriceTotal.THB,
                        MMK: acc.live.discountedPrice.MMK + liveDiscountedPriceTotal.MMK
                    },
                    discountAmount: {
                        THB: acc.live.discountAmount.THB + liveDiscountAmountTotal.THB,
                        MMK: acc.live.discountAmount.MMK + liveDiscountAmountTotal.MMK
                    },
                    sellerEarnings: {
                        THB: acc.live.sellerEarnings.THB + liveSellerEarningsTotal.THB,
                        MMK: acc.live.sellerEarnings.MMK + liveSellerEarningsTotal.MMK
                    }
                },
                snapshot: {
                    orgPrice: {
                        THB: acc.snapshot.orgPrice.THB + snapshotOriginalPriceTotal.THB,
                        MMK: acc.snapshot.orgPrice.MMK + snapshotOriginalPriceTotal.MMK
                    },
                    discountedPrice: {
                        THB: acc.snapshot.discountedPrice.THB + snapshotDiscountedPriceTotal.THB,
                        MMK: acc.snapshot.discountedPrice.MMK + snapshotDiscountedPriceTotal.MMK
                    },
                    discountAmount: {
                        THB: acc.snapshot.discountAmount.THB + snapshotDiscountAmountTotal.THB,
                        MMK: acc.snapshot.discountAmount.MMK + snapshotDiscountAmountTotal.MMK
                    }
                }
            };
        },
        {
            quantity: 0,
            live: {
                orgPrice: { THB: 0, MMK: 0 },
                discountedPrice: { THB: 0, MMK: 0 },
                discountAmount: { THB: 0, MMK: 0 },
                sellerEarnings: { THB: 0, MMK: 0 }
            },
            snapshot: {
                orgPrice: { THB: 0, MMK: 0 },
                discountedPrice: { THB: 0, MMK: 0 },
                discountAmount: { THB: 0, MMK: 0 }
            }
        }
    );
};

export const mergeOrderWithSnapshot = (rawOrders, countries, shippingFees) => {
    const priceFormatter = new PriceFormatter();
    const liveCurrencyConverter = new CurrencyConverter(countries);
    const livePriceCalculator = new PriceCalculator(priceFormatter, (thb) =>
        liveCurrencyConverter.convertTHBToMMK(thb)
    );

    const parsedOrders = rawOrders.map((rawOrder) => {
        const { orderRawJson, ...live } = rawOrder;
        const marketType = rawOrder.marketType || MARKET_TYPES.NEON;
        const isAffiliateMarketType = AFFILIATE_MARKET_TYPES.includes(marketType);

        let snapshot = null;
        try {
            snapshot = typeof orderRawJson === "string" ? JSON.parse(orderRawJson) : orderRawJson;
            snapshot = {
                ...snapshot,
                region: snapshot.paymentRegion || null,
                exchangeRate: snapshot.exchangeRate || null
            };
        } catch (error) {
            console.warn("Failed to parse orderRawJson:", error);
            return null;
        }

        const liveExchangeRate = live.exchangeRate || 1;
        const countriesWithSnapshotExchangeRate = countries.map((c) =>
            c.countryCode === "01" ? { ...c, exchangeRate: liveExchangeRate } : c
        );
        const snapshotCurrencyConverter = new CurrencyConverter(countriesWithSnapshotExchangeRate);
        const snapshotPriceCalculator = new PriceCalculator(priceFormatter, (thb) =>
            snapshotCurrencyConverter.convertTHBToMMK(thb)
        );

        const items = getOrderItems(
            snapshot.customerProductOrderDTOS,
            live.customerProductOrderDTOS,
            snapshotPriceCalculator,
            livePriceCalculator,
            priceFormatter
        );

        const snapshotShippingFee = {
            THB: snapshot.logisticsCharge || 0,
            MMK: snapshotCurrencyConverter.convertTHBToMMK(snapshot.logisticsCharge || 0)
        };

        const snapshotShippingFeeWithMinimum = isAffiliateMarketType
            ? snapshotShippingFee
            : snapshotPriceCalculator.applyMinTotalShippingFee(snapshotShippingFee);

        const { shippingFee: liveShippingFee } = calculateShippingFee(
            shippingFees,
            snapshot.customerShippingInfoDTO,
            snapshot.totalWeight,
            liveCurrencyConverter
        );

        const liveShippingFeeWithMinimum = isAffiliateMarketType
            ? liveShippingFee
            : livePriceCalculator.applyMinTotalShippingFee(liveShippingFee);

        const itemTotals = getItemTotals(items);

        return {
            id: snapshot.id,
            orderNumber: snapshot.orderNumber,
            createdDate: snapshot.createdDate,
            shippingAddress: snapshot.customerShippingInfoDTO,
            shop: {
                id: snapshot?.sellerDTO?.id || null,
                name: snapshot?.sellerDTO?.shopInfo?.shopName || "Unknown Shop",
                image: snapshot?.sellerDTO?.imagePath || "/images/default-shop.png"
            },
            customer: {
                id: snapshot.customerShippingInfoDTO.customerId,
                name: `${snapshot?.customerShippingInfoDTO?.firstName || ""} ${
                    snapshot?.customerShippingInfoDTO?.lastName || ""
                }`.trim(),
                phone: snapshot?.customerShippingInfoDTO?.phNumber || "No Phone No."
            },
            orderStatus: live.orderStatus,
            paymentStatus: live.paymentStatus,
            deliveryStatus: live.deliveryStatus,
            isReviewed: live.isReviewed,
            reason: live.reason,
            paymentRegion: live.paymentRegion,
            exchangeRate: liveExchangeRate,
            items,
            totals: {
                quantity: itemTotals.quantity,
                snapshot: {
                    orgPrice: itemTotals.snapshot.orgPrice,
                    discountedPrice: itemTotals.snapshot.discountedPrice,
                    discountAmount: itemTotals.snapshot.discountAmount,
                    shippingFee: snapshotShippingFeeWithMinimum,
                    grandTotalPrice: {
                        THB:
                            itemTotals.snapshot.discountedPrice.THB +
                            snapshotShippingFeeWithMinimum.THB,
                        MMK:
                            itemTotals.snapshot.discountedPrice.MMK +
                            snapshotShippingFeeWithMinimum.MMK
                    }
                },
                live: {
                    orgPrice: itemTotals.live.orgPrice,
                    discountedPrice: itemTotals.live.discountedPrice,
                    discountAmount: itemTotals.live.discountAmount,
                    sellerEarnings: itemTotals.live.sellerEarnings,
                    shippingFee: liveShippingFeeWithMinimum,
                    grandTotalPrice: {
                        THB: itemTotals.live.discountedPrice.THB + liveShippingFeeWithMinimum.THB,
                        MMK: itemTotals.live.discountedPrice.MMK + liveShippingFeeWithMinimum.MMK
                    }
                }
            },
            marketType: live.marketType || "Unknown",
            affiliateOrderData: live.affiliateOrderData || null
        };
    });

    const filteredOrders = parsedOrders.filter((order) => order !== null);

    return filteredOrders;
};

export const calculateShippingFee = (
    shippingFees,
    selectedAddress,
    chargeableWeight,
    currencyConverter
) => {
    const defaultReturn = {
        shippingFee: {
            THB: 0,
            MMK: 0
        },
        calculationDetails: {
            chargesPerKg: 0,
            minKg: 0,
            shippingFeeOverride: 0,
            exchangeRate: 0
        }
    };

    if (
        typeof shippingFees === "undefined" ||
        typeof selectedAddress === "undefined" ||
        typeof chargeableWeight === "undefined"
    ) {
        return defaultReturn;
    }
    if (
        !Array.isArray(shippingFees) ||
        typeof selectedAddress !== "object" ||
        selectedAddress === null ||
        typeof chargeableWeight !== "number" ||
        chargeableWeight <= 0 ||
        isNaN(chargeableWeight)
    ) {
        return defaultReturn;
    }
    if (shippingFees.length === 0) {
        return defaultReturn;
    }
    if (typeof selectedAddress.country !== "string") {
        return defaultReturn;
    }
    const selectedCountryName = selectedAddress.country.trim() || "Myanmar";
    const shippingFee = shippingFees.find((e) => e.countryName === selectedCountryName);
    if (!shippingFee) {
        return defaultReturn;
    }
    if (
        typeof shippingFee.chargesPerKg === "undefined" ||
        typeof shippingFee.minKg === "undefined" ||
        typeof shippingFee.shippingFeeOverride === "undefined"
    ) {
        return defaultReturn;
    }
    const chargesPerKg = parseFloat(shippingFee.chargesPerKg);
    const minKg = parseFloat(shippingFee.minKg);
    const shippingFeeOverride = parseFloat(shippingFee.shippingFeeOverride);
    if (isNaN(chargesPerKg) || isNaN(minKg) || isNaN(shippingFeeOverride)) {
        return defaultReturn;
    }
    if (chargesPerKg < 0 || minKg < 0 || shippingFeeOverride < 0) {
        return defaultReturn;
    }
    let fee = 0;
    if (chargeableWeight <= minKg) {
        fee = shippingFeeOverride;
    } else {
        fee = chargeableWeight * chargesPerKg;
    }
    const priceFormatter = new PriceFormatter();
    const roundedShippingFeeInTHB = priceFormatter.round(fee);

    const shippingFeeInTHB = Math.max(roundedShippingFeeInTHB, MIN_TOTAL_SHIPPING_FEE_THB);
    const roundedShippingFeeInMMK = priceFormatter.round(
        currencyConverter.convertTHBToMMK(shippingFeeInTHB)
    );
    const shippingFeeInMMK = Math.max(roundedShippingFeeInMMK, MIN_TOTAL_SHIPPING_FEE_MMK);

    return {
        shippingFee: {
            THB: shippingFeeInTHB,
            MMK: shippingFeeInMMK
        },
        calculationDetails: {
            chargesPerKg,
            minKg,
            shippingFeeOverride,
            exchangeRate: currencyConverter.getExchangeRate("MMK")
        }
    };
};
